import { Component, computed, EventEmitter, inject, Input, OnDestroy, OnInit, Output, input } from '@angular/core';
import { formatBytes } from '@tpt/common/utils';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { EditorComponent } from '@progress/kendo-angular-editor';
import type { UploadEntity } from '@tpt/common/inputs/drag-drop';
import { urlValidator } from '@app/shared/utils/validators';
import {
  InsertImageModalService,
  Image,
} from '@app/pages/main/pages/quality/components/shared/editor/modals/insert-image-modal/insert-image-modal.service';
import { Subscription } from 'rxjs';
import { DialogService } from '@app/services';
import { OrigamiResponseWithAuthUrl } from '@app/origami-api/services/e-signature.service';

enum InsertImageTabs {
  UploadImage = 'UPLOAD-IMAGE',
  AttachUrl = 'ATTACH-URL',
  ManageImages = 'MANAGE-IMAGES',
}

@Component({
  standalone: false,
  selector: 'ti-insert-image-modal',
  templateUrl: './insert-image-modal.component.html',
  styleUrls: ['./insert-image-modal.component.scss', '../insert-link-modal/insert-link-modal.component.scss'],
})
export class InsertImageModalComponent implements OnInit, OnDestroy {
  private insertImageModalService = inject(InsertImageModalService);
  private readonly dialogService = inject(DialogService);

  readonly miscStrings = tiStrings.app.misc;
  readonly strings = tiStrings.modules.quality.incidents;

  readonly imageDragDropFormatSupport = ['jpg', 'jpeg', 'png'];
  readonly maxImgSize = input<number>(5 * 1024 * 1024);

  readonly uploadDropDescription = computed(() => {
    const size = this.maxImgSize();
    const sizeDescription = size ? $localize`Maximum upload image size is ${formatBytes(size)}.` : '';

    const formats = this.imageDragDropFormatSupport;
    const formatsDescription = formats.length
      ? $localize`Formats supported are ${formats.map((f) => '.' + f).join(', ')}.`
      : '';

    return [sizeDescription, formatsDescription].filter(Boolean).join('\n');
  });

  insertImgTabs = InsertImageTabs;
  activeTab: InsertImageTabs = InsertImageTabs.UploadImage;
  uploadedUrlValue: string = '';
  uploadedImageFileName: string | null = null;
  form: FormGroup;
  images: Image[] = [];
  loading = false;
  private sub = new Subscription();

  @Input() editor: EditorComponent;
  @Output() close$ = new EventEmitter<boolean>();

  ngOnInit(): void {
    this.form = new FormGroup({
      url: new FormControl('', [Validators.required, urlValidator]),
    });
  }

  insert = (): void => {
    const { url } = this.form.value;
    this.insertImageIntoEditor(this.isUploadTab ? this.uploadedUrlValue : url);
    this.close();
  };

  close(): void {
    this.close$.emit(false);
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  getError(controlName: string): string | null {
    const control: AbstractControl = this.form.get(controlName);
    if (control && control.invalid && control.touched) {
      const { errors } = control;
      if (errors?.required) {
        return this.miscStrings.thisFieldIsRequired;
      }
      if (errors?.notUrl) {
        return this.strings.notValidURL;
      }
    }
    return null;
  }

  tabChange(data: InsertImageTabs) {
    this.activeTab = data;

    if (this.activeTab === this.insertImgTabs.ManageImages) {
      this.loadImages();
    }
  }

  onImageDropTransfer(entities: UploadEntity[]): void {
    const file = entities.find((e): e is File => e instanceof File);
    if (!file) {
      return;
    }
    this.sub.add(
      this.insertImageModalService.uploadSiteImage(file).subscribe((body) => {
        const url = body?.Result;
        if (url) {
          this.uploadedUrlValue = url;
          this.uploadedImageFileName = file.name;
        }
      }),
    );
  }

  onUploadedImageChipRemove(): void {
    // console.log('onUploadedImageChipRemove');
    this.uploadedUrlValue = '';
    this.uploadedImageFileName = null;
    this.form.patchValue({ url: '' });
    this.form.get('url')?.markAsUntouched();
    this.form.get('url')?.markAsPristine();
  }

  get isDisabledBtn(): boolean {
    return this.isUploadTab ? !this.uploadedUrlValue : !this.isFormValid;
  }

  get isUploadTab(): boolean {
    return this.activeTab === this.insertImgTabs.UploadImage;
  }

  get isManageImagesTab(): boolean {
    return this.activeTab === this.insertImgTabs.ManageImages;
  }

  loadImages(): void {
    this.loading = true;
    this.sub.add(
      this.insertImageModalService.loadImages().subscribe({
        next: (res: OrigamiResponseWithAuthUrl<Image[]>): void => {
          this.images = res.Result || [];
          this.loading = false;
        },
        error: (error): void => {
          // console.error('Error loading images', error);
          this.loading = false;
        },
      }),
    );
  }

  deleteImage(item: Image): void {
    this.dialogService
      .askYesNoQuestion(
        this.strings.deleteImage,
        this.strings.areYouSureWantDeleteImg.replace('{0}', item.Name),
        this.miscStrings.delete,
        this.miscStrings.cancel,
        null,
        true,
        null,
        null,
        null,
        true,
      )
      .then((data) => {
        if (data === 'yes') {
          this.loading = true;

          this.sub.add(
            this.insertImageModalService.deleteImage(item).subscribe({
              next: (): void => {
                this.loading = false;
                this.loadImages();
              },
              error: (error): void => {
                // console.error('Error deleting image', error);
                this.loading = false;
              },
            }),
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.sub && this.sub.unsubscribe();
  }

  insertImageIntoEditor(url: string): void {
    this.editor.exec('insertImage', { src: url, target: 'window' });
    this.close();
  }

  trackByFunc(index: number, image: Image): string {
    return image.Url;
  }
}
